import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the SideNavigation module
 */
export default class SideNavigation extends MessageBus {
  /**
   * Constructor function for the SideNavigation class.
   * @param {HTMLElement} element - The module's container element.
   */
  constructor(element) {
    super();
    this.element = element;

    this.selectElement = this.element.querySelector(
      `.${CLASS_NAMES.INTRODUCTION_LIST_SELECT}`
    );
    this.bodyContents = document.querySelectorAll(
      `.${CLASS_NAMES.BODY_TEXT_CONTENT}`
    );
    this.table = this.element.querySelector(
      `${SELECTORS.UL}.${CLASS_NAMES.INTRODUCTION_SUBSECTION_TERTIARY}`
    );

    // Bind methods to `this`
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleTertiaryNavClick = this.handleTertiaryNavClick.bind(this);

    // Initialize the navigation
    this.init();
  }

  /**
   * Initializes the view by setting up event listeners and handling navigation logic.
   * @returns {SideNavigation} A reference to the current instance of the class.
   */
  init() {
    this.enable();
    return this;
  }

  /**
   * Enable event listeners for interactions.
   * @returns {SideNavigation} A reference to the current instance of the class.
   */
  enable() {
    if (this.selectElement) {
      this.selectElement.addEventListener(
        EVENTS.CHANGE,
        this.handleSelectChange
      );
      return this;
    }

    // Check if any .body-text__content has <h2> elements
    const hasH2Elements = [...this.bodyContents].some(
      (bodyContent) =>
        bodyContent.querySelectorAll(`${SELECTORS.H2}`).length > 0
    );

    // Hide 4th-level navigation if no <h2> elements exist
    if (!hasH2Elements) {
      this.table.style.display = 'none';
      return this;
    }

    // Populate tertiary navigation
    this.populateTertiaryNavigation();

    // Add click event listener for navigation
    this.table.addEventListener(EVENTS.CLICK, this.handleTertiaryNavClick);

    return this;
  }

  /**
   * Handles the change event for the select dropdown.
   * @param {Event} event - The event object.
   */
  /* eslint-disable class-methods-use-this */
  handleSelectChange(event) {
    const url = event.target.value;
    if (url) {
      window.location.href = url;
    }
  }

  /**
   * Populates the tertiary navigation menu with detected <h2> elements from all .body-text__content sections.
   */
  populateTertiaryNavigation() {
    let counter = 1;

    this.bodyContents.forEach((bodyContent) => {
      const h2Elements = bodyContent.querySelectorAll(`${SELECTORS.H2}`);

      h2Elements.forEach((heading) => {
        heading.setAttribute('data-toc-id', counter);

        const listItem = this.table
          .querySelector(
            `.${CLASS_NAMES.INTRODUCTION_LIST_ITEM_TERTIARY}.${CLASS_NAMES.TEMPLATE}`
          )
          .cloneNode(true);
        const link = listItem.querySelector('a');

        link.setAttribute('data-toc-id', counter);
        link.innerHTML = heading.innerHTML;
        listItem.classList.remove(CLASS_NAMES.TEMPLATE);

        this.table.appendChild(listItem);
        counter += 1;
      });
    });
  }

  /**
   * Handles click event for tertiary navigation items.
   * @param {Event} event - The event object.
   */
  handleTertiaryNavClick(event) {
    event.preventDefault();

    const target = event.target.closest('a[data-toc-id]');
    if (!target) return;

    const id = target.getAttribute('data-toc-id');

    // Find the matching <h2> in any .body-text__content section
    let heading = null;
    this.bodyContents.forEach((bodyContent) => {
      const found = bodyContent.querySelector(
        `${SELECTORS.H2}[data-toc-id="${id}"]`
      );
      if (found) heading = found;
    });

    if (heading) {
      const top = heading.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: top - 120,
        behavior: 'smooth'
      });
    }
  }
}
